/**
 * Fingerprints — what makes two lines "the same line".
 *
 * `merchantFingerprint` is ported from StableBooks (src/shared/merchant-
 * fingerprint.ts) with its tests: card statements bury a per-transaction id in
 * the merchant string, so exact matching never learns anything.
 *
 * `transactionFingerprint` is NOT ported. StableBooks keys deduplication on the
 * bank's own reference number, which a CSV export does not carry and many
 * banks never print, so its dedup silently does nothing here. This is a content
 * hash instead — date, amount and normalised description — which every source
 * can produce.
 *
 * A match is a SUSPECTED duplicate and nothing more (spec ruling 4). Two coffees
 * on the same day at the same price are one fingerprint and two real expenses;
 * the owner decides. That is also why a hash is good enough: a collision costs
 * one glance at a review row, never a lost payment.
 *
 * Pure, synchronous and Node-free — crypto.subtle is async and would force
 * every caller, including a Svelte derived, to await a hash.
 */

/** True for a token that looks like a transaction id rather than a name. */
function isIdToken(token: string): boolean {
  const hasLetter = /[A-Z]/.test(token);
  const hasDigit = /\d/.test(token);
  if (token.length >= 5 && hasLetter && hasDigit) return true;
  if (token.length >= 4 && /^\d+$/.test(token)) return true;
  return false;
}

/**
 * A merchant string with the varying parts taken out:
 *   GRAB* A-9CPTWLJWW64AAV HA NOI  ->  GRAB HA NOI
 *   GRAB* A-9DXBNE5G29DSAV HA NOI  ->  GRAB HA NOI
 */
export function merchantFingerprint(raw: string): string {
  const cleaned = (raw ?? '')
    .toUpperCase()
    // Card networks mark a merchant as `GRAB*`; the asterisk carries no meaning.
    .replace(/\*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned === '') return '';

  const tokens = cleaned.split(' ');
  const kept = tokens.filter((t) => !isIdToken(t));
  // Everything looked like an id — keep the original rather than returning an
  // empty fingerprint, which would collide with every other such merchant.
  if (kept.length === 0) return cleaned;
  return kept.join(' ');
}

/* ==========================================================================
 * Hashing
 * ======================================================================== */

function fnv1a(text: string, seed: number): number {
  let hash = seed;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    // The FNV prime, multiplied in 16-bit halves so it stays exact in a double.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function hex8(n: number): string {
  return n.toString(16).padStart(8, '0');
}

/**
 * 64 bits of hash, as 16 hex characters. Two FNV-1a passes from different
 * offset bases: one 32-bit hash over a few hundred statement lines would see
 * collisions often enough to matter, two will not.
 */
function hash64(text: string): string {
  return hex8(fnv1a(text, 0x811c9dc5)) + hex8(fnv1a(text, 0x7fffffff));
}

/* ==========================================================================
 * The two fingerprints that get stored
 * ======================================================================== */

/** What identifies a transaction, whatever file it arrived in. */
export interface TransactionKey {
  incurred_on: string;
  amount: number;
  description: string;
}

/**
 * A stable content hash for one transaction line.
 *
 * Prefixed `t1-` on purpose: if the recipe ever changes, old fingerprints stop
 * matching new ones instead of matching them wrongly, and the prefix says which
 * generation a stored row belongs to.
 */
export function transactionFingerprint(key: TransactionKey): string {
  const cents = Math.round((Number(key.amount) || 0) * 100);
  // -0 and 0 are the same money.
  const amount = (cents === 0 ? 0 : cents).toString();
  const parts = [(key.incurred_on ?? '').trim(), amount, merchantFingerprint(key.description ?? '')];
  return `t1-${hash64(parts.join('|'))}`;
}

/**
 * A hash of a whole file's content, so a second import of the same download is
 * noticed. Content, not filename: `statement (1).csv` is the same statement.
 */
export function fileFingerprint(text: string): string {
  const body = (text ?? '').replace(/\r\n/g, '\n').trim();
  return `f1-${hash64(body)}-${body.length}`;
}
